/**
 * Which airport owns the desk Local clock.
 *
 * 1. Live on-surface field — sim is on the ground, taxiing, or just landed.
 * 2. Active trip origin — the ticket the desk is following. Accept updates this
 *    immediately when the airplane is not live on a surface.
 * 3. Parked / last-known field.
 * 4. Company base.
 *
 * A live surface sample wins over a trip you accepted somewhere else: taxiing
 * to another field moves Local without a restart. Zulu is never this clock.
 *
 * If the owning ICAO has no timezone on file, Local does not invent ADT. It
 * uses the next airport in that list that does have a zone and marks it
 * last-known (starred abbreviation), or it shows Zulu. The zone is that
 * field's civil timezone (curated table, otherwise the coordinate table).
 * It is not the PC clock, and the Gantt Local/Z toggle does not feed this.
 */
import { timeZoneForIcao } from "./airportTimeZone";
import { formatCardDateInZone, formatClockInZone, formatZoneCardTime, zoneAbbr } from "./clock";

const SURFACE_PHASES = new Set(["on_ground", "taxi", "landed"]);

export type DeskLocalSource = "live" | "ticket" | "parked" | "base" | "none";

export type DeskLocalFallback = "none" | "zulu" | "last-known";

export type DeskLocalClock = {
  /** ICAO that owns Local. Null when nothing is set. */
  icao: string | null;
  source: DeskLocalSource;
  /** IANA zone used to paint the clock. `UTC` when fail-closed to Zulu. */
  timeZone: string;
  /** Abbreviation on the Local card: `ADT`, `PDT`, `ADT*` (last-known), or `Z`. */
  zoneLabel: string;
  fallback: DeskLocalFallback;
  /** ICAO whose zone we painted when fallback is last-known. */
  fallbackIcao: string | null;
  /** Tooltip. Names the field, or says why Local is not that field's zone. */
  title: string;
};

export type ResolveDeskLocalInput = {
  now?: Date;
  /** Current field when the sim is live and on the surface. */
  liveSurfaceIcao?: string | null;
  /** Origin of the trip the desk is following. */
  ticketOriginIcao?: string | null;
  parkedIcao?: string | null;
  homeIcao?: string | null;
};

type Candidate = { source: Exclude<DeskLocalSource, "none">; icao: string };

function icaoOf(value?: string | null): string | null {
  const key = (value ?? "").trim().toUpperCase();
  return key || null;
}

function candidates(input: ResolveDeskLocalInput): Candidate[] {
  const rows: Candidate[] = [];
  const live = icaoOf(input.liveSurfaceIcao);
  const ticket = icaoOf(input.ticketOriginIcao);
  const parked = icaoOf(input.parkedIcao);
  const home = icaoOf(input.homeIcao);
  if (live) rows.push({ source: "live", icao: live });
  if (ticket) rows.push({ source: "ticket", icao: ticket });
  if (parked) rows.push({ source: "parked", icao: parked });
  if (home) rows.push({ source: "base", icao: home });
  return rows;
}

function zuluClock(icao: string | null, source: DeskLocalSource, title: string): DeskLocalClock {
  return {
    icao,
    source,
    timeZone: "UTC",
    zoneLabel: "Z",
    fallback: "zulu",
    fallbackIcao: null,
    title,
  };
}

/** Pure. Recompute when the driving airport changes — nothing is cached. */
export function resolveDeskLocalClock(input: ResolveDeskLocalInput = {}): DeskLocalClock {
  const now = input.now ?? new Date();
  const rows = candidates(input);
  const owner = rows[0];
  if (!owner) return zuluClock(null, "none", "No airport for Local — Zulu");

  const ownZone = timeZoneForIcao(owner.icao);
  if (ownZone) {
    return {
      icao: owner.icao,
      source: owner.source,
      timeZone: ownZone,
      zoneLabel: zoneAbbr(now, ownZone),
      fallback: "none",
      fallbackIcao: null,
      title: `${owner.icao} · ${ownZone}`,
    };
  }

  for (const row of rows.slice(1)) {
    const known = timeZoneForIcao(row.icao);
    if (!known) continue;
    const abbr = zoneAbbr(now, known);
    return {
      icao: owner.icao,
      source: owner.source,
      timeZone: known,
      zoneLabel: `${abbr}*`,
      fallback: "last-known",
      fallbackIcao: row.icao,
      title: `No timezone for ${owner.icao} — last-known ${row.icao} (${abbr})`,
    };
  }

  return zuluClock(owner.icao, owner.source, `No timezone for ${owner.icao} — Zulu`);
}

/**
 * Live surface field for Local. Airborne nearest-airport guesses are ignored
 * so a cruise over another zone does not move the clock. Stale / last-known
 * samples are not live.
 */
export function liveSurfaceIcao(
  state?: {
    phase?: string | null;
    stale?: boolean;
    source?: string | null;
    currentIcao?: string | null;
  } | null,
): string | null {
  if (!state || state.stale !== false) return null;
  if (state.source !== "simconnect" && state.source !== "sayintentions" && state.source !== "vatsim") return null;
  if (!state.phase || !SURFACE_PHASES.has(state.phase)) return null;
  return icaoOf(state.currentIcao);
}

/** Schedule toolbar hint. Names the field, or the fail-closed label. */
export function deskLocalScheduleHint(clock: DeskLocalClock): string {
  if (clock.fallback === "zulu") {
    return clock.icao ? `Local unavailable (${clock.icao}) — Z` : "Local unavailable — Z";
  }
  if (clock.fallback === "last-known") {
    return `Local last-known ${clock.fallbackIcao} — no timezone for ${clock.icao}`;
  }
  return `Local at ${clock.icao}`;
}

/** Short place for trip STD/STA Local copy. */
export function deskLocalPlace(clock: DeskLocalClock): string {
  if (clock.fallback === "zulu") {
    return clock.icao ? `Zulu — no timezone for ${clock.icao}` : "Zulu — no airport timezone";
  }
  if (clock.fallback === "last-known") {
    return `last-known ${clock.fallbackIcao} (no timezone for ${clock.icao})`;
  }
  return clock.icao ?? "the ops field";
}

export function formatDeskLocalClock(date: Date, clock: DeskLocalClock): string {
  return formatClockInZone(date, clock.timeZone, clock.zoneLabel);
}

export function formatDeskLocalCardTime(date: Date, clock: DeskLocalClock): string {
  return formatZoneCardTime(date, clock.timeZone);
}

export function formatDeskLocalCardDate(date: Date, clock: DeskLocalClock): string {
  return formatCardDateInZone(date, clock.timeZone);
}
